// src/pages/components/ReviewsTab.jsx

import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import axios from "axios";

/* =========================================================
   API
========================================================= */

const GET_REVIEWS_API =
    "https://oppiesfunc.azurewebsites.net/api/getReviewsByShop?code=DoHf7NWbvGWUlR3EADcU1aU9aM4pMnOYTivml1Ky27xRAzFuTKLPMQ==";

/* =========================================================
   HELPERS
========================================================= */

const normaliseRating = (value) => {
    const rating = Number(value);

    if (!Number.isFinite(rating)) {
        return 0;
    }

    return Math.min(
        5,
        Math.max(
            0,
            rating
        )
    );
};

const formatReviewDate = (value) => {
    if (!value) {
        return "";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "";
    }

    return date.toLocaleDateString(
        "en-NZ",
        {
            day: "numeric",
            month: "long",
            year: "numeric",
        }
    );
};

const relativeTime = (value) => {
    if (!value) {
        return "";
    }

    const timestamp =
        new Date(value).getTime();

    if (
        !Number.isFinite(
            timestamp
        )
    ) {
        return "";
    }

    const difference =
        Math.max(
            0,
            Date.now() -
            timestamp
        );

    const minute =
        60 * 1000;

    const hour =
        60 * minute;

    const day =
        24 * hour;

    if (
        difference <
        minute
    ) {
        return "Just now";
    }

    if (
        difference <
        hour
    ) {
        const minutes =
            Math.floor(
                difference /
                minute
            );

        return `${minutes} ${minutes === 1
                ? "minute"
                : "minutes"
            } ago`;
    }

    if (
        difference <
        day
    ) {
        const hours =
            Math.floor(
                difference /
                hour
            );

        return `${hours} ${hours === 1
                ? "hour"
                : "hours"
            } ago`;
    }

    if (
        difference <
        day * 30
    ) {
        const days =
            Math.floor(
                difference /
                day
            );

        return `${days} ${days === 1
                ? "day"
                : "days"
            } ago`;
    }

    return formatReviewDate(
        value
    );
};

/* =========================================================
   STAR DISPLAY
========================================================= */

function Stars({
    rating,
    size = 14,
}) {
    const safeRating =
        normaliseRating(
            rating
        );

    return (
        <div
            className="reviews-stars"
            aria-label={`${safeRating} out of 5 stars`}
        >
            {[
                1,
                2,
                3,
                4,
                5,
            ].map(
                (star) => (
                    <span
                        key={star}
                        className={
                            star <=
                                safeRating
                                ? "reviews-star-filled"
                                : "reviews-star-empty"
                        }
                        style={{
                            fontSize:
                                size,
                        }}
                    >
                        ★
                    </span>
                )
            )}
        </div>
    );
}

/* =========================================================
   COMPONENT
========================================================= */

function ReviewsTab({
    shopId,
}) {
    const [
        reviews,
        setReviews,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        refreshing,
        setRefreshing,
    ] = useState(false);

    const [
        error,
        setError,
    ] = useState("");

    /* =======================================================
       LOAD REVIEWS
    ======================================================= */

    const fetchReviews =
        useCallback(
            async ({
                showRefreshState = false,
            } = {}) => {
                if (!shopId) {
                    setReviews([]);
                    setLoading(false);

                    return;
                }

                try {
                    setError("");

                    if (
                        showRefreshState
                    ) {
                        setRefreshing(
                            true
                        );
                    } else {
                        setLoading(
                            true
                        );
                    }

                    const response =
                        await axios.get(
                            `${GET_REVIEWS_API}&shopId=${encodeURIComponent(shopId)}&_t=${Date.now()}`
                        );

                    const rawReviews =
                        Array.isArray(
                            response?.data
                                ?.reviews
                        )
                            ? response.data
                                .reviews
                            : [];

                    const cleaned =
                        rawReviews
                            .map(
                                (
                                    review,
                                    index
                                ) => ({
                                    ...review,

                                    _key:
                                        review?.id ||
                                        `${review?.reviewerEmail || "review"}_${review?.createdAt || index}`,

                                    rating:
                                        normaliseRating(
                                            review?.rating
                                        ),

                                    reviewerName:
                                        review?.reviewerName ||
                                        "OPPIES shopper",

                                    comment:
                                        review?.comment ||
                                        "",
                                })
                            )
                            .sort(
                                (a, b) => {
                                    const aTime =
                                        new Date(
                                            a?.createdAt ||
                                            0
                                        ).getTime();

                                    const bTime =
                                        new Date(
                                            b?.createdAt ||
                                            0
                                        ).getTime();

                                    return (
                                        (Number.isFinite(
                                            bTime
                                        )
                                            ? bTime
                                            : 0) -
                                        (Number.isFinite(
                                            aTime
                                        )
                                            ? aTime
                                            : 0)
                                    );
                                }
                            );

                    setReviews(
                        cleaned
                    );
                } catch (err) {
                    console.error(
                        "Failed to fetch reviews:",
                        err
                    );

                    setError(
                        "We couldn't load your reviews. Please try again."
                    );
                } finally {
                    setLoading(false);

                    setRefreshing(
                        false
                    );
                }
            },
            [shopId]
        );

    useEffect(() => {
        fetchReviews();
    }, [fetchReviews]);

    /* =======================================================
       STATS
    ======================================================= */

    const stats =
        useMemo(() => {
            const total =
                reviews.length;

            if (!total) {
                return {
                    total: 0,
                    average: 0,
                    distribution: {
                        5: 0,
                        4: 0,
                        3: 0,
                        2: 0,
                        1: 0,
                    },
                };
            }

            const sum =
                reviews.reduce(
                    (
                        current,
                        review
                    ) =>
                        current +
                        normaliseRating(
                            review.rating
                        ),
                    0
                );

            const distribution = {
                5: 0,
                4: 0,
                3: 0,
                2: 0,
                1: 0,
            };

            reviews.forEach(
                (review) => {
                    const rounded =
                        Math.round(
                            normaliseRating(
                                review.rating
                            )
                        );

                    if (
                        rounded >= 1 &&
                        rounded <= 5
                    ) {
                        distribution[
                            rounded
                        ] += 1;
                    }
                }
            );

            return {
                total,

                average:
                    sum / total,

                distribution,
            };
        }, [reviews]);

    /* =======================================================
       RENDER
    ======================================================= */

    return (
        <>
            <style>
                {reviewsStyles}
            </style>

            <div className="reviews-page">

                {/* ================================================
            INTRO
        ================================================ */}

                <section className="reviews-intro">

                    <div>

                        <div className="reviews-eyebrow">
                            Customer Feedback
                        </div>

                        <h1>
                            Reviews
                        </h1>

                        <p>
                            See what OPPIES
                            shoppers are saying
                            about your Op Shop.
                        </p>

                    </div>

                    <button
                        type="button"
                        className="reviews-refresh"
                        onClick={() =>
                            fetchReviews({
                                showRefreshState:
                                    true,
                            })
                        }
                        disabled={
                            refreshing ||
                            loading
                        }
                    >
                        <svg
                            className={
                                refreshing
                                    ? "reviews-spin"
                                    : ""
                            }
                            viewBox="0 0 24 24"
                            width="15"
                            height="15"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.9"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M20 6v5h-5" />

                            <path d="M4 18v-5h5" />

                            <path d="M18.5 9A7 7 0 0 0 6.7 6.5L4 9" />

                            <path d="M5.5 15a7 7 0 0 0 11.8 2.5L20 15" />
                        </svg>

                        {refreshing
                            ? "Refreshing..."
                            : "Refresh"}

                    </button>

                </section>

                {/* ================================================
            ERROR
        ================================================ */}

                {error ? (
                    <div className="reviews-error">

                        <div>
                            !
                        </div>

                        <span>
                            {error}
                        </span>

                        <button
                            type="button"
                            onClick={() =>
                                fetchReviews()
                            }
                        >
                            Try Again
                        </button>

                    </div>
                ) : null}

                {/* ================================================
            LOADING
        ================================================ */}

                {loading ? (
                    <div className="reviews-loading">

                        <div className="reviews-loader" />

                        <strong>
                            Loading reviews
                        </strong>

                        <span>
                            Checking your latest
                            customer feedback...
                        </span>

                    </div>
                ) : reviews.length ===
                    0 ? (
                    /* ==============================================
                       EMPTY
                    ============================================== */

                    <div className="reviews-empty">

                        <div className="reviews-empty-icon">

                            <svg
                                viewBox="0 0 24 24"
                                width="28"
                                height="28"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="m12 2 3.1 6.3 6.9 1-5 4.9 1.2 6.8-6.2-3.2L5.8 21 7 14.2l-5-4.9 6.9-1Z" />
                            </svg>

                        </div>

                        <strong>
                            No reviews yet
                        </strong>

                        <span>
                            Reviews from OPPIES
                            shoppers will appear here
                            once your shop receives
                            them.
                        </span>

                    </div>
                ) : (
                    <>
                        {/* ============================================
                OVERVIEW
            ============================================ */}

                        <section className="reviews-overview">

                            <div className="reviews-score">

                                <div className="reviews-score-number">
                                    {stats.average.toFixed(
                                        1
                                    )}
                                </div>

                                <Stars
                                    rating={
                                        Math.round(
                                            stats.average
                                        )
                                    }
                                    size={16}
                                />

                                <div className="reviews-score-caption">
                                    Based on{" "}
                                    {stats.total}{" "}
                                    {stats.total ===
                                        1
                                        ? "review"
                                        : "reviews"}
                                </div>

                            </div>

                            <div className="reviews-breakdown">

                                {[
                                    5,
                                    4,
                                    3,
                                    2,
                                    1,
                                ].map(
                                    (rating) => {
                                        const count =
                                            stats
                                                .distribution[
                                            rating
                                            ];

                                        const percentage =
                                            stats.total
                                                ? (count /
                                                    stats.total) *
                                                100
                                                : 0;

                                        return (
                                            <div
                                                key={
                                                    rating
                                                }
                                                className="reviews-breakdown-row"
                                            >

                                                <div className="reviews-breakdown-label">
                                                    <span>
                                                        {rating}
                                                    </span>

                                                    <span className="reviews-small-star">
                                                        ★
                                                    </span>
                                                </div>

                                                <div className="reviews-progress">

                                                    <div
                                                        style={{
                                                            width: `${percentage}%`,
                                                        }}
                                                    />

                                                </div>

                                                <span className="reviews-breakdown-count">
                                                    {count}
                                                </span>

                                            </div>
                                        );
                                    }
                                )}

                            </div>

                        </section>

                        {/* ============================================
                LIST HEADING
            ============================================ */}

                        <div className="reviews-list-heading">

                            <div>

                                <div className="reviews-list-eyebrow">
                                    Feedback
                                </div>

                                <h2>
                                    Customer reviews
                                </h2>

                            </div>

                            <span>
                                {stats.total}{" "}
                                {stats.total ===
                                    1
                                    ? "review"
                                    : "reviews"}
                            </span>

                        </div>

                        {/* ============================================
                REVIEW LIST
            ============================================ */}

                        <div className="reviews-list">

                            {reviews.map(
                                (review) => {
                                    const initial =
                                        (
                                            review.reviewerName ||
                                            "O"
                                        )
                                            .slice(
                                                0,
                                                1
                                            )
                                            .toUpperCase();

                                    const when =
                                        relativeTime(
                                            review.createdAt
                                        );

                                    const fullDate =
                                        formatReviewDate(
                                            review.createdAt
                                        );

                                    return (
                                        <article
                                            key={
                                                review._key
                                            }
                                            className="review-card"
                                        >

                                            <div className="review-card-header">

                                                <div className="review-person">

                                                    <div className="review-avatar">
                                                        {
                                                            initial
                                                        }
                                                    </div>

                                                    <div>

                                                        <strong>
                                                            {
                                                                review.reviewerName
                                                            }
                                                        </strong>

                                                        {when ? (
                                                            <span
                                                                title={
                                                                    fullDate
                                                                }
                                                            >
                                                                {
                                                                    when
                                                                }
                                                            </span>
                                                        ) : null}

                                                    </div>

                                                </div>

                                                <div className="review-rating">

                                                    <Stars
                                                        rating={
                                                            review.rating
                                                        }
                                                        size={
                                                            13
                                                        }
                                                    />

                                                    <span>
                                                        {
                                                            review.rating
                                                        }
                                                        /5
                                                    </span>

                                                </div>

                                            </div>

                                            {review.comment ? (
                                                <div className="review-comment">

                                                    <svg
                                                        viewBox="0 0 24 24"
                                                        width="15"
                                                        height="15"
                                                        fill="currentColor"
                                                        aria-hidden="true"
                                                    >
                                                        <path d="M7.3 6C4.5 6 3 8 3 10.6V18h7v-7H6.6c.2-1.4 1-2.3 2.7-2.8L7.3 6Zm10 0C14.5 6 13 8 13 10.6V18h7v-7h-3.4c.2-1.4 1-2.3 2.7-2.8L17.3 6Z" />
                                                    </svg>

                                                    <p>
                                                        {
                                                            review.comment
                                                        }
                                                    </p>

                                                </div>
                                            ) : (
                                                <div className="review-no-comment">
                                                    This shopper left
                                                    a rating without a
                                                    written comment.
                                                </div>
                                            )}

                                        </article>
                                    );
                                }
                            )}

                        </div>
                    </>
                )}

            </div>
        </>
    );
}

export default ReviewsTab;

/* =========================================================
   STYLES
========================================================= */

const reviewsStyles = `

  .reviews-page {
    color: #1f2937;
    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  /* ======================================================
     INTRO
  ====================================================== */

  .reviews-intro {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 22px;
  }

  .reviews-eyebrow,
  .reviews-list-eyebrow {
    color: #047857;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .reviews-intro h1 {
    margin: 7px 0;
    color: #17211c;
    font-size:
      clamp(
        27px,
        3vw,
        37px
      );
    line-height: 1.08;
    letter-spacing: -0.035em;
  }

  .reviews-intro p {
    max-width: 620px;
    margin: 0;
    color: #64748b;
    font-size: 14px;
    line-height: 1.65;
  }

  .reviews-refresh {
    display: inline-flex;
    min-height: 37px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 8px 12px;
    border: 1px solid #d4dfda;
    border-radius: 10px;
    background: #ffffff;
    color: #047857;
    cursor: pointer;
    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
  }

  .reviews-refresh:hover:not(:disabled) {
    background: #f0fdf4;
  }

  .reviews-refresh:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  /* ======================================================
     ERROR
  ====================================================== */

  .reviews-error {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-bottom: 15px;
    padding: 10px 12px;
    border: 1px solid #fecaca;
    border-radius: 12px;
    background: #fef2f2;
    color: #b91c1c;
    font-size: 9px;
    font-weight: 600;
  }

  .reviews-error > div {
    display: grid;
    width: 23px;
    height: 23px;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 50%;
    background: #fee2e2;
    font-weight: 900;
  }

  .reviews-error span {
    flex: 1;
  }

  .reviews-error button {
    padding: 6px 9px;
    border: 1px solid #efb7b7;
    border-radius: 7px;
    background: #ffffff;
    color: #b91c1c;
    cursor: pointer;
    font-family: inherit;
    font-size: 8px;
    font-weight: 800;
  }

  /* ======================================================
     OVERVIEW
  ====================================================== */

  .reviews-overview {
    display: grid;
    grid-template-columns:
      minmax(180px, 0.65fr)
      minmax(280px, 1.35fr);
    gap: 14px;
    margin-bottom: 23px;
  }

  .reviews-score,
  .reviews-breakdown {
    box-sizing: border-box;
    padding: 19px;
    border: 1px solid #e2e8e4;
    border-radius: 18px;
    background: #ffffff;
    box-shadow:
      0 5px 18px
      rgba(
        15,
        23,
        42,
        0.03
      );
  }

  .reviews-score {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .reviews-score-number {
    color: #17211c;
    font-size: 44px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.055em;
  }

  .reviews-score .reviews-stars {
    margin-top: 9px;
  }

  .reviews-score-caption {
    margin-top: 7px;
    color: #94a3b8;
    font-size: 8px;
    font-weight: 600;
  }

  /* ======================================================
     STARS
  ====================================================== */

  .reviews-stars {
    display: inline-flex;
    align-items: center;
    gap: 1px;
    line-height: 1;
  }

  .reviews-star-filled {
    color: #d39b32;
  }

  .reviews-star-empty {
    color: #dfe5e2;
  }

  /* ======================================================
     BREAKDOWN
  ====================================================== */

  .reviews-breakdown {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
  }

  .reviews-breakdown-row {
    display: grid;
    grid-template-columns:
      32px
      minmax(0, 1fr)
      25px;
    align-items: center;
    gap: 8px;
  }

  .reviews-breakdown-label {
    display: flex;
    align-items: center;
    gap: 3px;
    color: #64748b;
    font-size: 8px;
    font-weight: 800;
  }

  .reviews-small-star {
    color: #d39b32;
    font-size: 9px;
  }

  .reviews-progress {
    overflow: hidden;
    height: 7px;
    border-radius: 999px;
    background: #edf1ef;
  }

  .reviews-progress > div {
    height: 100%;
    border-radius: inherit;
    background: #047857;
    transition: width 0.3s ease;
  }

  .reviews-breakdown-count {
    color: #94a3b8;
    font-size: 7px;
    font-weight: 700;
    text-align: right;
  }

  /* ======================================================
     LIST HEADING
  ====================================================== */

  .reviews-list-heading {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 11px;
  }

  .reviews-list-heading h2 {
    margin: 4px 0 0;
    color: #17211c;
    font-size: 19px;
    letter-spacing: -0.025em;
  }

  .reviews-list-heading > span {
    color: #94a3b8;
    font-size: 8px;
    font-weight: 700;
  }

  /* ======================================================
     REVIEW CARDS
  ====================================================== */

  .reviews-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .review-card {
    padding: 16px;
    border: 1px solid #e5eae7;
    border-radius: 16px;
    background: #ffffff;
    box-shadow:
      0 4px 15px
      rgba(
        15,
        23,
        42,
        0.025
      );
  }

  .review-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
  }

  .review-person {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 9px;
  }

  .review-avatar {
    display: grid;
    width: 35px;
    height: 35px;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 50%;
    background: #ecfdf5;
    color: #047857;
    font-size: 11px;
    font-weight: 800;
  }

  .review-person strong {
    display: block;
    overflow: hidden;
    color: #334155;
    font-size: 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .review-person span {
    display: block;
    margin-top: 2px;
    color: #94a3b8;
    font-size: 7px;
  }

  .review-rating {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 7px;
  }

  .review-rating > span {
    color: #8b9891;
    font-size: 7px;
    font-weight: 800;
  }

  /* ======================================================
     COMMENT
  ====================================================== */

  .review-comment {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    margin-top: 13px;
    padding: 12px 13px;
    border-radius: 11px;
    background: #fafcfb;
  }

  .review-comment svg {
    flex: 0 0 auto;
    margin-top: 1px;
    color: #a9b7b0;
  }

  .review-comment p {
    margin: 0;
    color: #52605a;
    font-size: 10px;
    font-style: italic;
    line-height: 1.6;
    white-space: pre-wrap;
  }

  .review-no-comment {
    margin-top: 11px;
    color: #a0aaa5;
    font-size: 8px;
    font-style: italic;
  }

  /* ======================================================
     LOADING / EMPTY
  ====================================================== */

  .reviews-loading,
  .reviews-empty {
    display: flex;
    min-height: 300px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 25px;
    border: 1px solid #e5eae7;
    border-radius: 18px;
    background: #ffffff;
    text-align: center;
  }

  .reviews-loader {
    width: 29px;
    height: 29px;
    margin-bottom: 11px;
    border: 3px solid #d6eee5;
    border-top-color: #047857;
    border-radius: 50%;
    animation:
      reviews-rotate
      0.8s
      linear
      infinite;
  }

  .reviews-loading strong,
  .reviews-empty strong {
    color: #53615a;
    font-size: 10px;
  }

  .reviews-loading span,
  .reviews-empty span {
    max-width: 270px;
    margin-top: 4px;
    color: #94a3b8;
    font-size: 8px;
    line-height: 1.5;
  }

  .reviews-empty-icon {
    display: grid;
    width: 54px;
    height: 54px;
    margin-bottom: 11px;
    place-items: center;
    border-radius: 16px;
    background: #eef5f1;
    color: #759388;
  }

  .reviews-spin {
    animation:
      reviews-rotate
      0.8s
      linear
      infinite;
  }

  @keyframes reviews-rotate {
    to {
      transform:
        rotate(360deg);
    }
  }

  /* ======================================================
     RESPONSIVE
  ====================================================== */

  @media (max-width: 680px) {

    .reviews-overview {
      grid-template-columns: 1fr;
    }

    .reviews-intro {
      align-items: flex-start;
      flex-direction: column;
    }

    .reviews-refresh {
      align-self: flex-start;
    }

  }

  @media (max-width: 480px) {

    .review-card-header {
      align-items: flex-start;
      flex-direction: column;
    }

    .review-rating {
      padding-left: 44px;
    }

  }

`;